<template>
  <v2-card>
    <template #header>
      <span class="v2-font-semibold v2-text-sm">Details</span>
    </template>
    <div class="v2-order-addresses">
      <div class="v2-order-addresses__row">
        <span class="v2-order-addresses__label">Collection Name:</span>
        <span class="v2-order-addresses__value">{{ order.collectionName }}</span>
      </div>
      <!-- Notes -->
      <div class="v2-order-addresses__row">
        <span class="v2-order-addresses__label">Notes:</span>
        <template v-if="!isEditNotes">
          <span class="v2-order-addresses__value">
            {{ notes || '-' }}
            <button
              v-if="isRetailer"
              class="v2-order-addresses__edit-btn"
              @click="isEditNotes = true"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
              </svg>
            </button>
          </span>
        </template>
        <template v-if="isEditNotes">
          <div class="v2-order-addresses__notes-edit">
            <textarea
              v-model="notes"
              class="v2-order-addresses__textarea"
              placeholder="Enter Notes"
              rows="3"
            />
            <v2-button
              size="sm"
              variant="primary"
              :disabled="order.notes === notes"
              @click="onUpdateNotes(order)"
            >
              Save
            </v2-button>
          </div>
        </template>
      </div>
      <!-- Brand info -->
      <template v-if="isBrand">
        <div class="v2-order-addresses__row">
          <span class="v2-order-addresses__label">Retailer Name:</span>
          <span class="v2-order-addresses__value">
            {{ order.retailerName }}
            <template v-if="order.retailerBrandIdentity">
              ({{ order.retailerBrandIdentity }})
            </template>
          </span>
        </div>
        <div
          v-if="order.retailerFirstName"
          class="v2-order-addresses__row"
        >
          <span class="v2-order-addresses__label">Buyer Name:</span>
          <span class="v2-order-addresses__value">{{ order.retailerFirstName }}</span>
        </div>
      </template>
      <!-- Billing Address -->
      <template v-if="isRetailer && hasBillingAddress(order)">
        <div class="v2-order-addresses__section">
          <span class="v2-order-addresses__label">Billing Address:</span>
          <p class="v2-order-addresses__address">
            {{ order.billingAddressForRetailer.billingContactName }},
            {{ order.billingAddressForRetailer.billingContactNumber }},
            {{ order.billingAddressForRetailer.billingAddressLine1 }},
            <template v-if="order.billingAddressForRetailer.billingAddressLine2">
              {{ order.billingAddressForRetailer.billingAddressLine2 }},
            </template>
            {{ order.billingAddressForRetailer.billingCity }},
            {{ order.billingAddressForRetailer.billingCountry }}
            - {{ order.billingAddressForRetailer.billingZipcode }}
          </p>
        </div>
      </template>
      <!-- Shipping Address -->
      <template v-if="hasShippingAddress(order)">
        <div class="v2-order-addresses__section">
          <span class="v2-order-addresses__label">Shipping Address:</span>
          <p class="v2-order-addresses__address">
            {{ order.shippingAddressForRetailer.shippingContactName }},
            {{ order.shippingAddressForRetailer.shippingContactNumber }},
            {{ order.shippingAddressForRetailer.shippingAddressLine1 }},
            <template v-if="order.shippingAddressForRetailer.shippingAddressLine2">
              {{ order.shippingAddressForRetailer.shippingAddressLine2 }},
            </template>
            {{ order.shippingAddressForRetailer.shippingCity }},
            {{ order.shippingAddressForRetailer.shippingCountry }}
            - {{ order.shippingAddressForRetailer.shippingZipcode }}
          </p>
        </div>
      </template>
    </div>
  </v2-card>
</template>

<script>
import V2Card from '@/components/v2/V2Card.vue'
import V2Button from '@/components/v2/V2Button.vue'
import { useOrderAddresses } from '@/composables/useOrderAddresses'

export default {
  name: 'V2OrderAddresses',
  components: { V2Card, V2Button },
  props: {
    order: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const addresses = useOrderAddresses()
    addresses.initNotes(props.order)
    return { ...addresses }
  },
}
</script>

<style lang="scss" scoped>
.v2-order-addresses {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;

  &__row {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
    font-size: 0.8125rem;
  }

  &__section {
    margin-top: 0.25rem;
    font-size: 0.8125rem;
  }

  &__label {
    color: var(--v2-muted-foreground);
    white-space: nowrap;
  }

  &__value {
    font-weight: 600;
    color: var(--v2-foreground);
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
  }

  &__edit-btn {
    background: none;
    border: none;
    padding: 2px;
    cursor: pointer;
    color: var(--v2-muted-foreground);
    display: inline-flex;
    align-items: center;

    &:hover {
      color: var(--v2-primary);
    }
  }

  &__notes-edit {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    width: 100%;
  }

  // Glass notes textarea — matches V2Input recipe so it sits in the same
  // visual language as the rest of the v2 form controls.
  &__textarea {
    width: 100%;
    padding: 0.5rem;
    border: 1px solid var(--v2-glass-stroke);
    border-radius: 0.5rem;
    font-size: 0.8125rem;
    font-family: inherit;
    color: var(--v2-foreground);
    background: var(--v2-glass-surface-2);
    resize: vertical;
    outline: none;
    transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover:not(:focus) {
      border-color: var(--v2-glass-stroke-strong);
    }

    &:focus {
      border-color: var(--v2-primary);
      box-shadow: 0 0 0 3px oklch(0.62 0.21 295 / 0.25);
    }
  }

  &__address {
    font-weight: 600;
    color: var(--v2-foreground);
    margin: 0.25rem 0 0;
    font-size: 0.8125rem;
    line-height: 1.5;
  }
}
</style>
